// The password policy is enforced by Auth0, not here. Two of its rules cannot be
// checked in the browser at all: the common-password dictionary, and whether the
// password contains the user's own personal data. So this module deliberately
// does not try to be the gate -- it checks only the rules it can check exactly,
// to spare the user a round trip, and leaves the rest to the server, whose
// refusal arrives as a `password-rejected` code with a `reason`.
//
// The one thing this must never do is reject a password Auth0 would accept. That
// is why the personal-data rule is not approximated here: we do not know how
// Auth0 tokenises a name or how short a fragment it will match, and guessing
// would refuse valid passwords.

const PASSWORD_MIN_LENGTH = 10;

// Three of these four classes, not all four. The auth0 dashboard cannot settle
// this -- its strength setting is a ladder that displays each level's mandate as
// an addition, so at "Excellent" it lists special characters as though required
// -- but a real attempt against the tenant settles it: a password with lower
// case, upper case and numbers and no symbol is accepted, so all four are not
// required.
//
// That confirms three classes are enough; it does not prove three is the minimum
// rather than two. Auth0 documents no two-class level, so requiring three here is
// the reading to hold, but a report of a valid two-class password being refused
// would point at this line.
const CHARACTER_CLASSES = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/];
const REQUIRED_CLASS_COUNT = 3;

const REPEATED_CHARACTER = /(.)\1\1/;

// The resting state summarises rather than reciting the policy; the specific rule
// that was broken is better said once it has actually been broken.
const PASSWORD_SUMMARY = 'At least 10 characters, and not easily guessable.';

type PasswordProblem =
  'too-short' | 'repeated-characters' | 'not-enough-character-classes';

// Returns the first rule the password breaks, or undefined if it passes
// everything checkable here -- which is not the same as Auth0 accepting it.
const checkPassword = (password: string): PasswordProblem | undefined => {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return 'too-short';
  }
  if (REPEATED_CHARACTER.test(password)) {
    return 'repeated-characters';
  }
  const classes = CHARACTER_CLASSES.filter((c) => c.test(password)).length;
  if (classes < REQUIRED_CLASS_COUNT) {
    return 'not-enough-character-classes';
  }
  return undefined;
};

const PROBLEM_MESSAGES: Record<PasswordProblem, string> = {
  'too-short': 'Use at least 10 characters.',
  'repeated-characters': 'Don’t repeat a character three times in a row.',
  'not-enough-character-classes':
    'Use at least three of: lower case, upper case, numbers, symbols.',
};

// Reasons the server can return alongside a `password-rejected` code. An
// unrecognised reason falls back to the summary, which stays true even when we
// cannot say which specific rule was broken.
const REJECTION_MESSAGES: Record<string, string> = {
  'too-short': PROBLEM_MESSAGES['too-short'],
  'too-weak':
    'That password is too weak. Try a longer mix of upper and lower case, numbers or symbols.',
  'too-common': 'That password is too common. Choose something less guessable.',
  'contains-personal-data':
    'That password contains your own details. Choose something unrelated to your name or email.',
};

const passwordProblemMessage = (problem: PasswordProblem): string =>
  PROBLEM_MESSAGES[problem];

const passwordRejectionMessage = (reason?: string): string =>
  (reason && REJECTION_MESSAGES[reason]) || PASSWORD_SUMMARY;

// True when an error from any of the password-carrying endpoints is the policy
// refusing the password, rather than a dead token or a service failure.
const isPasswordRejection = (e: any): boolean =>
  e?.data?.code === 'password-rejected';

const passwordRejectionReason = (e: any): string | undefined => e?.data?.reason;

export {
  PASSWORD_MIN_LENGTH,
  PASSWORD_SUMMARY,
  checkPassword,
  isPasswordRejection,
  passwordProblemMessage,
  passwordRejectionMessage,
  passwordRejectionReason,
};
export type { PasswordProblem };
